// Lógica pura do pop-up de bom-dia das horas — sem React, sem antd, sem rede
// (o jest do CRA não carrega o antd v6; aqui dá para testar de verdade).
//
// Pedido do Lucas (01/10/2026): na primeira vez que a pessoa mexe no sistema no
// dia (os computadores ficam ligados de um dia para o outro), um pop-up sugere
// por onde começar a anotar: o centro de custo do setor (Administrativo,
// Comercial) ou, para a operação, o destino mais usado nos últimos 7 dias.
// QUEM decide a sugestão é o servidor (dre-engine/handlers/horasSugestao.js);
// aqui só se decide QUANDO perguntar e como dizer.
import { formatarHoras } from '../pages/operacional-horas/horasUtils';

export type DestinoSugerido = {
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  rotulo: string;
  detalhe: string;
  minutos_recentes?: number;
};

export type SugestaoDoDia = {
  hoje: string;
  usuario_email: string;
  nome: string | null;
  rateio_fixo: boolean;
  sugerir: boolean;
  porque_nao: string | null;
  origem: 'setor' | 'recente' | null;
  destino: DestinoSugerido | null;
};

/** Uma pergunta por pessoa por dia: o que fica guardado no navegador. */
export const CHAVE_BOM_DIA = 'horas.bomdia';
export type RegistroBomDia = { dia: string; email: string };

/** Depois de uma falha de rede, espera isto antes de tentar de novo (o mouse
 *  mexe o tempo todo — sem a espera seria uma chamada por movimento). */
export const ESPERA_APOS_FALHA_MS = 5 * 60 * 1000;

/** O dia LOCAL de quem está na frente do computador, 'YYYY-MM-DD'. */
export function diaLocal(agora: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${agora.getFullYear()}-${p2(agora.getMonth() + 1)}-${p2(agora.getDate())}`;
}

export function lerRegistro(texto: string | null | undefined): RegistroBomDia | null {
  if (!texto) return null;
  try {
    const r = JSON.parse(texto);
    return r && typeof r.dia === 'string' && typeof r.email === 'string' ? { dia: r.dia, email: r.email } : null;
  } catch { return null; }
}

/** Já perguntamos a ESTA pessoa HOJE? Outro login no mesmo navegador é outra pessoa. */
export function jaPerguntouHoje(registro: RegistroBomDia | null, dia: string, email: string): boolean {
  return !!registro && registro.dia === dia && registro.email.toLowerCase() === email.toLowerCase();
}

/** O e-mail de dentro do token de login (JWT), sem conferir assinatura — serve só
 *  para saber de quem é o registro do navegador. null se o token não abrir. */
export function emailDoToken(token: string | null | undefined): string | null {
  try {
    const meio = String(token || '').split('.')[1];
    if (!meio) return null;
    const base64 = meio.replace(/-/g, '+').replace(/_/g, '/');
    const cheio = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    // atob devolve um byte por caractere; o percent-encoding remonta o UTF-8 (nome com acento).
    const texto = decodeURIComponent(Array.from(atob(cheio), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
    const dados = JSON.parse(texto);
    return typeof dados.email === 'string' && dados.email ? dados.email.toLowerCase() : null;
  } catch { return null; }
}

/** "Bom dia, Lucas" — pela hora do computador da pessoa. */
export function saudacao(hora: number, nome: string | null): string {
  const base = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
  return nome ? `${base}, ${nome}` : base;
}

/** Por que este destino — a frase embaixo do botão. */
export function motivoDaSugestao(s: Pick<SugestaoDoDia, 'origem' | 'destino'>): string {
  if (!s.destino) return 'Escolha onde você está trabalhando para o cronômetro começar a contar.';
  if (s.origem === 'setor') return `${s.destino.detalhe}.`;
  const min = Number(s.destino.minutos_recentes) || 0;
  return min > 0
    ? `Foi onde você mais anotou nos últimos 7 dias (${formatarHoras(min)}).`
    : 'Foi onde você mais anotou nos últimos 7 dias.';
}

/** O corpo do POST que liga o cronômetro: contrato OU centro de custo, nunca os dois. */
export function corpoDoCronometro(d: DestinoSugerido): { contrato_id: string } | { centro_custo_codigo: string } {
  if (d.contrato_id) return { contrato_id: d.contrato_id };
  return { centro_custo_codigo: String(d.centro_custo_codigo) };
}

/** O pop-up só abre com resposta do servidor mandando sugerir. Resposta estranha = não abre. */
export function deveAbrir(s: Partial<SugestaoDoDia> | null | undefined): boolean {
  return !!s && s.sugerir === true && !s.rateio_fixo;
}
